import { AxiosError } from 'axios';
import {
  MADINA_BRANCHES,
  MADINA_CART,
  MADINA_CART_ITEMS,
  MADINA_CART_REVALIDATE,
  MADINA_CATALOG_CATEGORIES,
  MADINA_CATALOG_HOME,
  MADINA_CATALOG_PRODUCTS,
  MADINA_CHECKOUT,
  MADINA_CHECKOUT_PAYMENT_CONFIG,
  MADINA_CHECKOUT_PAYMENT_METHODS,
  MADINA_HEALTH,
  MADINA_ORDERS,
  MADINA_SEARCH_PRODUCTS,
  MADINA_SEARCH_SUGGESTIONS,
  madinaCartItemPath,
  madinaOrderCancelPath,
  madinaOrderPath,
  madinaOrderPaymentStatusPath,
  madinaOrderPaymentVerifyPath,
  madinaOrderReceiptPath,
  madinaOrderReceiptPdfPath,
  madinaOrderTrackingPath,
  madinaProductDetailPath
} from 'network/config/madinaEndpoints';
import api from 'network/config/setAuthHeader';
import { unwrapMadina } from 'network/utils/madinaResponse';
import type {
  MadinaBranch,
  MadinaCart,
  MadinaCartItem,
  MadinaCatalogHome,
  MadinaCategory,
  MadinaCheckoutPayload,
  MadinaCheckoutResponse,
  MadinaOrder,
  MadinaPaymentConfig,
  MadinaPaymentMethod, MadinaPaymentStatusResponse, MadinaProduct,
  MadinaProductListParams,
  MadinaSearchParams,
  MadinaTracking
} from 'TYPES/madina';
import { clearCardIdempotencyKey, getMadinaBrowserCallbackUrl, isOnionPayWebhookUrl } from 'utils/madinaPayment';
import {
  extractMadinaOrderPaymentStatus,
  isFailedPaymentStatus,
  isMadinaOrderPaidFromPoll
} from 'utils/madinaPaymentStatus';

const normalizeMadinaCartItem = (item: Record<string, unknown>): MadinaCartItem => {
  const nested = item.product as MadinaProduct | undefined;
  const productName = String(
    nested?.name ?? item.product_name ?? item.productName ?? item.name ?? ''
  ).trim();
  const quantity = Number(item.quantity ?? item.qty ?? 1);
  const cartItemId = Number(
    item.cart_item_id ?? item.cartItemId ?? item.id ?? item.cart_line_id ?? item.cartLineId ?? 0
  );
  const productId = Number(item.product_id ?? item.productId ?? nested?.id ?? 0);
  const product =
    nested?.name || !productName
      ? nested
      : ({ ...(nested ?? {}), id: productId, name: productName } as MadinaProduct);
  const availableFlag = item.available ?? item.is_available ?? item.in_stock;

  return {
    id: Number.isFinite(cartItemId) && cartItemId > 0 ? cartItemId : undefined,
    cart_item_id: Number.isFinite(cartItemId) && cartItemId > 0 ? cartItemId : undefined,
    product_id: productId,
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    available: typeof availableFlag === 'boolean' ? availableFlag : undefined,
    available_qty: Number(item.available_qty ?? item.availableQty ?? product?.available_qty ?? 0) || undefined,
    product,
    unit_price: Number(
      item.unit_price ??
      item.unitPrice ??
      item.price ??
      item.amount ??
      product?.price ??
      product?.min_price ??
      0
    ) || undefined,
    line_total: Number(item.line_total ?? item.lineTotal ?? item.subtotal ?? item.total ?? 0) || undefined,
    price: item.price as number | undefined,
    amount: item.amount as number | undefined
  };
};

const normalizeMadinaCart = (data: Record<string, unknown>): MadinaCart => {
  const itemsRaw = (data.items ?? data.cart_items ?? data.lines ?? []) as Record<string, unknown>[];
  const items = itemsRaw.map(normalizeMadinaCartItem);

  return {
    items,
    item_count: Number(data.item_count ?? data.itemCount ?? items.length),
    subtotal: Number(data.subtotal ?? data.sub_total ?? 0) || undefined,
    sub_total: Number(data.sub_total ?? 0) || undefined,
    total: Number(data.total ?? data.total_amount ?? 0) || undefined,
    total_amount: Number(data.total_amount ?? 0) || undefined,
    all_available: data.all_available as boolean | undefined,
    branch_id: Number(data.branch_id ?? data.branchId ?? 0) || undefined
  };
};

const parseMadinaCartResponse = async (
  data: Record<string, unknown>,
  branchId: number
): Promise<MadinaCart> => {
  const itemsRaw = data.items ?? data.cart_items ?? data.lines;
  if (Array.isArray(itemsRaw)) {
    return normalizeMadinaCart(data);
  }
  return getMadinaCart(branchId);
};

export const madinaHealthCheck = async () => {
  const response = await api.get(MADINA_HEALTH);
  return unwrapMadina(response);
};

export const getMadinaBranches = async (businessId?: number): Promise<MadinaBranch[]> => {
  const response = await api.get(MADINA_BRANCHES, {
    params: businessId ? { business_id: businessId } : undefined
  });
  const data = unwrapMadina<MadinaBranch[] | { branches?: MadinaBranch[] }>(response);
  return Array.isArray(data) ? data : data.branches ?? [];
};

export const getMadinaCatalogHome = async (branchId: number): Promise<MadinaCatalogHome> => {
  const response = await api.get(MADINA_CATALOG_HOME, { params: { branch_id: branchId } });
  return unwrapMadina(response);
};

const MADINA_CATEGORIES_CACHE_TTL_MS = 15 * 60 * 1000;
let madinaCategoriesCache: { expiresAt: number; categories: MadinaCategory[] } | null = null;
let madinaCategoriesInflight: Promise<MadinaCategory[]> | null = null;

export const getMadinaCategories = async (): Promise<MadinaCategory[]> => {
  if (madinaCategoriesCache && madinaCategoriesCache.expiresAt > Date.now()) {
    return madinaCategoriesCache.categories;
  }

  if (!madinaCategoriesInflight) {
    madinaCategoriesInflight = (async () => {
      const response = await api.get(MADINA_CATALOG_CATEGORIES);
      const data = unwrapMadina<MadinaCategory[] | { categories?: MadinaCategory[] }>(response);
      const categories = Array.isArray(data) ? data : data.categories ?? [];
      madinaCategoriesCache = {
        expiresAt: Date.now() + MADINA_CATEGORIES_CACHE_TTL_MS,
        categories
      };
      madinaCategoriesInflight = null;
      return categories;
    })().catch(error => {
      madinaCategoriesInflight = null;
      throw error;
    });
  }

  return madinaCategoriesInflight;
};

const readMadinaEnvelopeMeta = (response: { data?: Record<string, unknown> }): Record<string, unknown> | undefined => {
  const payload = response.data;
  if (!payload || typeof payload !== 'object') return undefined;
  if ('meta' in payload && payload.meta) {
    return payload.meta as Record<string, unknown>;
  }
  return undefined;
};

export const getMadinaProducts = async (
  params: MadinaProductListParams
): Promise<{ products: MadinaProduct[]; meta?: Record<string, unknown> }> => {
  const response = await api.get(MADINA_CATALOG_PRODUCTS, { params });
  const data = unwrapMadina<MadinaProduct[] | { products?: MadinaProduct[]; items?: MadinaProduct[] }>(response);
  const products = Array.isArray(data) ? data : data.products ?? data.items ?? [];
  return { products, meta: readMadinaEnvelopeMeta(response) };
};

const MADINA_CATALOG_PAGE_SIZE = 50;
const MADINA_CATALOG_CACHE_TTL_MS = 10 * 60 * 1000;

type MadinaCatalogCacheEntry = {
  expiresAt: number;
  products: MadinaProduct[];
};

const madinaCatalogCache = new Map<string, MadinaCatalogCacheEntry>();

const mergeMadinaProducts = (target: Map<number, MadinaProduct>, incoming: MadinaProduct[]) => {
  for (const product of incoming) {
    const id = Number(product.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    const existing = target.get(id);
    if (!existing) {
      target.set(id, product);
      continue;
    }
    const existingQty = existing.available_qty ?? 0;
    const nextQty = product.available_qty ?? 0;
    if (nextQty > existingQty) {
      target.set(id, product);
    }
  }
};

const fetchMadinaProductsForBranch = async (
  branchId: number,
  filters: Omit<MadinaProductListParams, 'branch_id' | 'page' | 'per_page'>,
  maxPages = 200
): Promise<MadinaProduct[]> => {
  const collected: MadinaProduct[] = [];
  let page = 1;

  while (page <= maxPages) {
    const { products, meta } = await getMadinaProducts({
      ...filters,
      branch_id: branchId,
      page,
      per_page: MADINA_CATALOG_PAGE_SIZE
    });

    if (!products.length) break;
    collected.push(...products);

    const pagination = meta?.pagination as { total_pages?: number } | undefined;
    if (pagination?.total_pages != null) {
      if (page >= pagination.total_pages) break;
    } else if (products.length < MADINA_CATALOG_PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return collected;
};

export type MadinaCatalogFetchOptions = {
  branchIds?: number[];
  maxPagesPerBranch?: number;
};

export const getMadinaProductsAllBranches = async (
  filters: Omit<MadinaProductListParams, 'branch_id' | 'page' | 'per_page'> = {},
  options?: MadinaCatalogFetchOptions
): Promise<MadinaProduct[]> => {
  const branchIds =
    options?.branchIds?.filter(id => Number.isFinite(id) && id > 0) ??
    (await getMadinaBranches()).map(branch => branch.id);
  const maxPages = options?.maxPagesPerBranch ?? 200;
  const merged = new Map<number, MadinaProduct>();

  await Promise.all(
    branchIds.map(async branchId => {
      const branchProducts = await fetchMadinaProductsForBranch(branchId, filters, maxPages);
      mergeMadinaProducts(merged, branchProducts);
    })
  );

  return Array.from(merged.values());
};

export const getCachedMadinaProductsAllBranches = async (
  cacheKey: string,
  filters: Omit<MadinaProductListParams, 'branch_id' | 'page' | 'per_page'> = {},
  options?: MadinaCatalogFetchOptions
): Promise<MadinaProduct[]> => {
  const scopedKey = options?.branchIds?.length
    ? `${cacheKey}@branches=${options.branchIds.join(',')}`
    : cacheKey;
  const cached = madinaCatalogCache.get(scopedKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.products;
  }

  const products = await getMadinaProductsAllBranches(filters, options);
  madinaCatalogCache.set(scopedKey, {
    expiresAt: Date.now() + MADINA_CATALOG_CACHE_TTL_MS,
    products
  });
  return products;
};

export const getMadinaProductDetail = async (productId: number, branchId: number): Promise<MadinaProduct> => {
  const response = await api.get(madinaProductDetailPath(productId), { params: { branch_id: branchId } });
  const data = unwrapMadina<MadinaProduct | { product?: MadinaProduct }>(response);
  if (data && typeof data === 'object' && 'product' in data && data.product) {
    return data.product;
  }
  return data as MadinaProduct;
};

export const searchMadinaProducts = async (
  params: MadinaSearchParams
): Promise<{ products: MadinaProduct[]; meta?: Record<string, unknown> }> => {
  const response = await api.get(MADINA_SEARCH_PRODUCTS, { params });
  const data = unwrapMadina<MadinaProduct[] | { products?: MadinaProduct[]; items?: MadinaProduct[] }>(response);
  const products = Array.isArray(data) ? data : data.products ?? data.items ?? [];
  return { products, meta: (response.data as { meta?: Record<string, unknown> })?.meta };
};

export const searchMadinaProductsAllBranches = async (
  q: string,
  opts?: { limit?: number; category_id?: number }
): Promise<MadinaProduct[]> => {
  const branches = await getMadinaBranches();
  const merged = new Map<number, MadinaProduct>();
  const limit = opts?.limit ?? 80;

  await Promise.all(
    branches.map(async branch => {
      const { products } = await searchMadinaProducts({
        branch_id: branch.id,
        q,
        category_id: opts?.category_id,
        page: 1,
        limit
      });
      mergeMadinaProducts(merged, products);
    })
  );

  return Array.from(merged.values());
};

export const searchMadinaSuggestions = async (branchId: number, q: string, limit = 10): Promise<string[]> => {
  const response = await api.get(MADINA_SEARCH_SUGGESTIONS, {
    params: { branch_id: branchId, q, limit }
  });
  const data = unwrapMadina<string[] | { suggestions?: string[] }>(response);
  return Array.isArray(data) ? data : data.suggestions ?? [];
};

export const getMadinaCart = async (branchId: number): Promise<MadinaCart> => {
  try {
    const response = await api.get(MADINA_CART, { params: { branch_id: branchId } });
    const data = unwrapMadina<Record<string, unknown>>(response);
    const cart = normalizeMadinaCart(data);
    if (!cart.branch_id) cart.branch_id = branchId;
    return cart;
  } catch (error) {
    const status = (error as AxiosError)?.response?.status;
    const message = (error as AxiosError<{ message?: string }>)?.response?.data?.message ?? '';
    if (status === 404 || /cart not found/i.test(message)) {
      return { items: [], item_count: 0, branch_id: branchId, all_available: true };
    }
    throw error;
  }
};

export const addMadinaCartItem = async (payload: {
  branch_id: number;
  product_id: number;
  quantity: number;
}): Promise<MadinaCart> => {
  const response = await api.post(MADINA_CART_ITEMS, payload);
  const data = unwrapMadina<Record<string, unknown>>(response);
  return normalizeMadinaCart(data);
};

export const resolveMadinaCartItemId = (cart: MadinaCart, productId: number): number => {
  const line = cart.items.find((item: MadinaCartItem) => item.product_id === productId);
  const cartItemId = line?.cart_item_id ?? line?.id;
  if (!cartItemId) {
    throw new Error('Cart line not found for this product');
  }
  return cartItemId;
};

export const findMadinaCartItemId = async (branchId: number, productId: number): Promise<number> => {
  const cart = await getMadinaCart(branchId);
  return resolveMadinaCartItemId(cart, productId);
};

export const updateMadinaCartItem = async (
  cartItemId: number,
  payload: { quantity: number; branch_id?: number }
): Promise<MadinaCart> => {
  const response = await api.patch(madinaCartItemPath(cartItemId), payload);
  const data = unwrapMadina<Record<string, unknown>>(response);
  const branchId = payload.branch_id ?? Number(data.branch_id ?? data.branchId ?? 0);
  return parseMadinaCartResponse(data, branchId);
};

export const removeMadinaCartItem = async (branchId: number, cartItemId: number): Promise<MadinaCart> => {
  const response = await api.delete(madinaCartItemPath(cartItemId));
  const data = unwrapMadina<Record<string, unknown>>(response);
  return parseMadinaCartResponse(data, branchId);
};

export const clearMadinaCart = async (branchId: number): Promise<MadinaCart> => {
  const cart = await getMadinaCart(branchId);
  if (!cart.items.length) {
    return cart;
  }

  let latestCart = cart;
  for (const item of [...cart.items]) {
    const cartItemId = item.cart_item_id ?? item.id;
    if (!cartItemId) continue;

    try {
      latestCart = await removeMadinaCartItem(branchId, cartItemId);
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;
      const message = (error as AxiosError<{ message?: string }>)?.response?.data?.message ?? '';
      if (status === 404 || /cart not found/i.test(message)) {
        latestCart = await getMadinaCart(branchId);
        continue;
      }
      throw error;
    }
  }

  return latestCart;
};

export const revalidateMadinaCart = async (payload: {
  branch_id: number;
  items?: Array<{ product_id: number; quantity: number }>;
}): Promise<MadinaCart> => {
  const response = await api.post(MADINA_CART_REVALIDATE, payload);
  const data = unwrapMadina<Record<string, unknown>>(response);
  const cart = normalizeMadinaCart(data);
  if (!cart.branch_id) cart.branch_id = payload.branch_id;
  return cart;
};

export const getMadinaPaymentMethods = async (branchId: number): Promise<MadinaPaymentMethod[]> => {
  const response = await api.get(MADINA_CHECKOUT_PAYMENT_METHODS, { params: { branch_id: branchId } });
  const data = unwrapMadina<MadinaPaymentMethod[] | { methods?: MadinaPaymentMethod[]; payment_methods?: MadinaPaymentMethod[] }>(
    response
  );
  if (Array.isArray(data)) return data;
  return data.methods ?? data.payment_methods ?? [];
};

export const getMadinaPaymentConfig = async (): Promise<MadinaPaymentConfig> => {
  const response = await api.get(MADINA_CHECKOUT_PAYMENT_CONFIG);
  return unwrapMadina(response);
};

export const madinaCheckout = async (payload: MadinaCheckoutPayload): Promise<MadinaCheckoutResponse> => {
  const response = await api.post(MADINA_CHECKOUT, payload, {
    headers: { 'Idempotency-Key': payload.idempotency_key }
  });
  return unwrapMadina(response);
};

export const getMadinaOrder = async (orderId: number): Promise<MadinaOrder> => {
  const response = await api.get(madinaOrderPath(orderId));
  const data = unwrapMadina<MadinaOrder | { order?: MadinaOrder }>(response);
  if (data && typeof data === 'object' && 'order' in data && data.order) {
    return data.order;
  }
  return data as MadinaOrder;
};

export const getMadinaOrders = async (params?: {
  status?: string;
  per_page?: number;
  page?: number;
}): Promise<MadinaOrder[]> => {
  const response = await api.get(MADINA_ORDERS, { params });
  const data = unwrapMadina<MadinaOrder[] | { orders?: MadinaOrder[]; items?: MadinaOrder[] }>(response);
  return Array.isArray(data) ? data : data.orders ?? data.items ?? [];
};

export const getMadinaPaymentStatus = async (orderId: number): Promise<MadinaPaymentStatusResponse> => {
  const response = await api.get(madinaOrderPaymentStatusPath(orderId));
  return unwrapMadina(response);
};

export const verifyMadinaPayment = async (
  orderId: number,
  payload: { reference: string; gateway?: string }
): Promise<MadinaPaymentStatusResponse> => {
  const response = await api.post(madinaOrderPaymentVerifyPath(orderId), payload);
  return unwrapMadina(response);
};

export const getMadinaOrderTracking = async (orderId: number): Promise<MadinaTracking> => {
  const response = await api.get(madinaOrderTrackingPath(orderId));
  return unwrapMadina(response);
};

export const getMadinaOrderReceipt = async (orderId: number) => {
  const response = await api.get(madinaOrderReceiptPath(orderId));
  const data = unwrapMadina<{ receipt?: { order_date?: string; [key: string]: unknown } }>(response);
  const receipt = data?.receipt ?? {};
  return {
    ...data,
    receipt: {
      ...receipt,
      order_date: typeof receipt.order_date === 'string' ? receipt.order_date : ''
    }
  };
};

export const getMadinaOrderReceiptPdfUrl = (orderId: number) => {
  const base = process.env.REACT_APP_BASE_URL ?? '';
  return `${base}${madinaOrderReceiptPdfPath(orderId)}`;
};

export const cancelMadinaOrder = async (orderId: number, note?: string) => {
  const response = await api.post(madinaOrderCancelPath(orderId), { note: note ?? 'Cancelled by customer' });
  return unwrapMadina(response);
};

/** One GET of payment-status. Does not treat the browser return or a verify 422 as paid or failed. */
export const checkMadinaPaymentStatus = async (orderId: number): Promise<MadinaPaymentStatusResponse> => {
  const status = await getMadinaPaymentStatus(orderId);
  const paymentStatus = extractMadinaOrderPaymentStatus(status);

  if (isFailedPaymentStatus(paymentStatus)) {
    throw new Error('Payment failed');
  }

  return status;
};

/** OnionPay return: poll GET payment-status only. About every 2s for up to 90s. */
export const pollMadinaPaymentStatus = async (
  orderId: number,
  options?: {
    maxAttempts?: number;
    delayMs?: number;
    onStatus?: (status: string) => void;
  }
): Promise<MadinaPaymentStatusResponse> => {
  const maxAttempts = options?.maxAttempts ?? 45;
  const delayMs = options?.delayMs ?? 2000;

  let latest: MadinaPaymentStatusResponse = {};

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    latest = await getMadinaPaymentStatus(orderId);
    const paymentStatus = extractMadinaOrderPaymentStatus(latest);
    options?.onStatus?.(paymentStatus);

    if (isMadinaOrderPaidFromPoll(latest)) {
      const stockTaken = latest.order?.inventory_deducted === true;
      if (stockTaken || attempt === maxAttempts - 1) {
        return latest;
      }
    }
    if (isFailedPaymentStatus(paymentStatus)) {
      throw new Error('Payment failed');
    }

    if (attempt < maxAttempts - 1) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return latest;
};

/** OnionPay browser return URL sent on checkout (`callback_url`). ERP webhook is server-side only. */
export { getMadinaBrowserCallbackUrl as getMadinaCallbackUrl } from 'utils/madinaPayment';

const readSession = (key: string, legacyKey: string) =>
  sessionStorage.getItem(key) ?? sessionStorage.getItem(legacyKey);

export const persistMadinaPendingPayment = (payload: {
  orderId: number;
  reference: string;
  gateway: string;
}) => {
  sessionStorage.setItem('madina_order_id', String(payload.orderId));
  sessionStorage.setItem('madina_reference', payload.reference);
  sessionStorage.setItem('madina_gateway', payload.gateway);
  sessionStorage.removeItem('madina_pending_order_id');
  sessionStorage.removeItem('madina_pending_reference');
  sessionStorage.removeItem('madina_pending_gateway');
};

/** Session saved before redirect. Query params on the return URL are not payment proof. */
export const readMadinaPendingPayment = () => ({
  orderId: readSession('madina_order_id', 'madina_pending_order_id'),
  reference: readSession('madina_reference', 'madina_pending_reference'),
  gateway: readSession('madina_gateway', 'madina_pending_gateway')
});

export const clearMadinaPendingPayment = () => {
  sessionStorage.removeItem('madina_order_id');
  sessionStorage.removeItem('madina_reference');
  sessionStorage.removeItem('madina_gateway');
  sessionStorage.removeItem('madina_pending_order_id');
  sessionStorage.removeItem('madina_pending_reference');
  sessionStorage.removeItem('madina_pending_gateway');
};

const readHostedPaymentLink = (payment: MadinaCheckoutResponse['payment'] | undefined): string => {
  if (!payment) return '';
  const raw = payment as Record<string, unknown>;
  const candidates = [
    payment.authorization_url,
    raw.authorizationUrl,
    raw.checkout_url,
    raw.checkoutUrl,
    raw.payment_url,
    raw.paymentUrl,
    raw.redirect_url,
    raw.redirectUrl
  ];
  const link = candidates.find(value => typeof value === 'string' && /^https?:\/\//i.test(value.trim()));
  return typeof link === 'string' ? link.trim() : '';
};

const isStorefrontCallback = (url: string): boolean => {
  try {
    const path = new URL(url).pathname.replace(/\/$/, '');
    return path.endsWith('/checkout/callback') || isOnionPayWebhookUrl(url);
  } catch {
    return false;
  }
};

/** Leave checkout only for a hosted OnionPay link. Never send the shopper to the callback page from here. */
export const startMadinaCardCheckout = async (
  payment: MadinaCheckoutResponse['payment'],
  orderId: number,
  options?: { duplicate?: boolean }
) => {
  const gateway = String(payment?.gateway ?? '').toLowerCase();
  const reference = payment?.reference?.trim() ?? '';
  const paymentLink = readHostedPaymentLink(payment);
  const linkIsOnionPay = /onionpay/i.test(paymentLink);
  const gatewayIsOnionPay = gateway === '' || gateway === 'onionpay';

  if (!paymentLink || isStorefrontCallback(paymentLink) || (!gatewayIsOnionPay && !linkIsOnionPay)) {
    if (options?.duplicate) {
      persistMadinaPendingPayment({
        orderId,
        reference: reference || `order-${orderId}`,
        gateway: 'onionpay'
      });
      window.location.assign(getMadinaBrowserCallbackUrl());
      return;
    }
    clearMadinaPendingPayment();
    clearCardIdempotencyKey();
    throw new Error(
      'OnionPay checkout did not open. Stay on this page and tap Make Payment again.'
    );
  }

  persistMadinaPendingPayment({
    orderId,
    reference: reference || `order-${orderId}`,
    gateway: 'onionpay'
  });
  window.location.assign(paymentLink);
};
